import { timestampDate } from '@bufbuild/protobuf/wkt'

import { ProcessImageType } from '@ai-color-modules/gen_proto/ai_color_simulation/v1/messages_pb'
import type { OriginalColorSimulationImages } from '@ai-color-modules/gen_proto/api_proto/v1/ai_color_simulation_pb'

/**
 * ベースエリア名とマスク画像タイプの対応
 */
const AREA_MASK_TYPE_MAP: Record<string, ProcessImageType> = {
  外壁: ProcessImageType.MASKED_WALL,
  屋根: ProcessImageType.MASKED_ROOF,
  雨樋: ProcessImageType.MASKED_GUTTER,
  '破風・鼻隠し': ProcessImageType.MASKED_FASCIA,
}

type SimulatedImage = NonNullable<OriginalColorSimulationImages['simulatedImage']>[number]

const getUploadedAtMs = (image: SimulatedImage) => {
  const uploadedAt = image.uploadedAt ? timestampDate(image.uploadedAt) : undefined
  return uploadedAt ? uploadedAt.getTime() : undefined
}

const getLatestMaskImage = (images: SimulatedImage[], maskType: ProcessImageType) => {
  return images.reduce<SimulatedImage | undefined>((latestImage, image) => {
    if (image.type !== maskType || !image.url) {
      return latestImage
    }
    if (!latestImage) {
      return image
    }

    const currentUploadedAt = getUploadedAtMs(image)
    const latestUploadedAt = getUploadedAtMs(latestImage)

    if (typeof currentUploadedAt === 'number' && typeof latestUploadedAt === 'number') {
      return currentUploadedAt >= latestUploadedAt ? image : latestImage
    }

    if (typeof currentUploadedAt === 'number') {
      return image
    }

    if (typeof latestUploadedAt === 'number') {
      return latestImage
    }

    return image
  }, undefined)
}

/**
 * OriginalColorSimulationImagesからベースエリアごとのマスク画像URLを抽出する
 *
 * @returns Record<エリア名, マスク画像URL> (例: { "外壁": "https://...", "屋根": "https://..." })
 */
export const getMaskUrls = (
  image: OriginalColorSimulationImages | undefined,
): Record<string, string> => {
  if (!image?.simulatedImage) return {}

  const result: Record<string, string> = {}
  for (const [areaName, maskType] of Object.entries(AREA_MASK_TYPE_MAP)) {
    const maskImage = getLatestMaskImage(image.simulatedImage, maskType)
    if (maskImage?.url) {
      result[areaName] = maskImage.url
    }
  }
  return result
}
